import classNames from "classnames";
import { AnimatePresence, motion } from "motion/react";
import uniq from "lodash/uniq";
import { Link } from "react-router-dom";
import { useBjelinTranslations } from "../../hooks/TranslationHooks";
import { FaqCategory, FaqTag } from "../../types/Faq";
import { useLang } from "../../hooks/LangHooks";
import { faqUrl, useFaqData } from "../../hooks/FaqHooks";
import { stripHtml } from "string-strip-html";
import "./style.scss";
import { useUser } from "../../functional-components/UserContext";
import CockpitEditLink from "../CockpitEditLink";

const FaqPageSection = ({
  category,
  tag = undefined
}: {
  category: FaqCategory;
  tag?: FaqTag;
}) => {
  const t = useBjelinTranslations();
  const { admin } = useUser();
  const lang = useLang();
  const faqData = useFaqData(lang);
  const articles = faqData.articles.filter(
    article => article.categories.indexOf(category._id) >= 0
  );

  const articleTags = uniq(articles.flatMap(article => article.tags));
  const tags = faqData.tags.filter(tag => articleTags.indexOf(tag._id) >= 0);

  const filteredArticles = tag
    ? articles.filter(article => article.tags.indexOf(tag._id) >= 0)
    : articles;

  return (
    <>
      <motion.div
        className="intro"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {category.name && (
          <h2>
            {category.name}
            {admin && (
              <span className={"text-muted text-small"}>
                {" "}
                ({articles.length})
              </span>
            )}
          </h2>
        )}
        {category.description && <p>{category.description}</p>}
        <CockpitEditLink
          collection={"faq_categories"}
          _id={category._id}
          labelKey={"edit.faq_category"}
          wrapper={children => <>{children}</>}
        />
      </motion.div>
      {tags.length > 0 && (
        <motion.ul
          className="tag-links"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <li className={classNames({ selected: !tag })} key="all-tags">
            <Link to={faqUrl({ category })}>{t("faq.all-questions")}</Link>
          </li>
          {tags.map(t => (
            <li
              className={classNames({ selected: t.id === tag?.id })}
              key={t.id}
            >
              <Link to={faqUrl({ category, tag: t })}>{t.name}</Link>
            </li>
          ))}
        </motion.ul>
      )}
      {tag && (
        <CockpitEditLink
          collection={"faq_tags"}
          _id={tag._id}
          labelKey={"edit.faq_tag"}
          wrapper={children => <>{children}</>}
        />
      )}
      <motion.ul
        className="faq-items"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          staggerChildren: 0.2
        }}
      >
        <AnimatePresence>
          {filteredArticles.map(item => (
            <motion.li
              className="faq-item-thumb"
              key={item.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1, transition: { duration: 0.5 } }}
              transition={{
                duration: 0.3
              }}
            >
              <Link to={faqUrl({ category, article: item })}>
                <div className="text-container">
                  <h3>{item.title}</h3>
                  <p>
                    {admin && item.promoted_article && (
                      <span
                        className={"text-danger"}
                        style={{ cursor: "help" }}
                        title={"Promoted"}
                      >
                        *
                      </span>
                    )}
                    {stripHtml(item.description, {}).result.substring(0, 125)}
                    ... ({t("read.more")})
                  </p>
                </div>
              </Link>
            </motion.li>
          ))}
        </AnimatePresence>
      </motion.ul>
    </>
  );
};

export default FaqPageSection;
